import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
    CommunityPickerDialogComponent,
    CommunityPickerItem,
} from '@interacta-shared/ui-community-picker';
import { CommunityType } from '@interacta-shared/util-caronte';
import { ICommunity } from '@interacta/util-community';
import { SidebarWorkspace } from '@modules/sidebar/models/sidebar.model';
import { filterWorkspaces } from '@modules/sidebar/models/sidebar.utils';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
    selector: 'interacta-community-selection-dialog',
    templateUrl: './community-selection-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommunityPickerDialogComponent],
})
export class CommunitySelectionDialogComponent {
    readonly workspaces = input.required<SidebarWorkspace[]>();
    readonly isOpen = input(false);
    readonly recentCommunities = input<ICommunity[]>();
    readonly emptyLabel = input('UI.NO_RESULT');
    readonly communityTypeFilter = input<CommunityType | undefined>(undefined);

    readonly closeDialog = output();
    readonly selectedCommunity = output<ICommunity>();

    readonly nameFilterControl = new FormControl('', { nonNullable: true });

    private readonly nameFilter = toSignal(
        this.nameFilterControl.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
        ),
        { initialValue: '' },
    );

    readonly communitiesTotalCount = computed(() =>
        this.workspaces()
            .map((w) => w.communities?.length ?? 0)
            .reduce((a, b) => a + b, 0),
    );

    readonly pickerWorkspaces = computed(() => {
        const nameFilter = this.nameFilter().toLowerCase();
        return this.workspaces()
            .map((workspace) =>
                filterWorkspaces(
                    workspace,
                    nameFilter,
                    true,
                    true,
                    this.communityTypeFilter(),
                ),
            )
            .filter((workspace) => (workspace?.communities ?? []).length)
            .map((w) => ({
                id: w.id,
                name: w.name as unknown as string,
                communities: (w.communities ?? []).map((c) => ({
                    id: c.id,
                    name: c.name as unknown as string,
                })),
            }));
    });

    readonly pickerRecentCommunities = computed(() =>
        (this.recentCommunities() ?? [])
            .filter(
                (c) =>
                    !this.communityTypeFilter() ||
                    c.type === this.communityTypeFilter(),
            )
            .map((c) => ({
                id: c.id,
                name: c.name as unknown as string,
            })),
    );

    constructor() {
        toObservable(this.communitiesTotalCount)
            .pipe(
                filter((count) => count <= 20),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.nameFilterControl.reset());
    }

    onCommunitySelected(picked: CommunityPickerItem): void {
        const community = this.workspaces()
            .flatMap((w) => w.communities ?? [])
            .find((c) => c.id === picked.id);
        if (community) {
            this.selectedCommunity.emit(community);
        }
    }
}
